import { useEffect, useState } from "react";

import type { BookerEvent } from "@calcom/features/bookings/types";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Button } from "@calcom/ui/components/button";
import { useFlags } from "@calcom/web/modules/feature-flags/hooks/useFlags";
import { useNoAvailabilityWithinThreshold } from "@calcom/web/modules/schedules/hooks/useNoAvailabilityWithinThreshold";
import type { Slots } from "@calcom/web/modules/schedules/lib/types";

import { WaitlistRegistrationDialog } from "./WaitlistRegistrationDialog";

/**
 * Detection window used when the acte only configured a fallback message (no waitlist threshold):
 * the "no availability" hook needs a bounded horizon to decide the schedule is empty. Mirrors the
 * default suggested when enabling the waitlist threshold in the event limits tab.
 */
const DEFAULT_FALLBACK_THRESHOLD_DAYS = 14;

type WaitlistCTAProps = {
  event:
    | (Pick<BookerEvent, "id" | "title" | "waitlistThresholdDays" | "noAvailabilityFallbackMessage"> & {
        // W-04 — only the PRESENCE of an owner matters here (a cabinet event type has none, and the
        // matching job could then never build a booking link). Declaring the minimum instead of
        // `Pick<BookerEvent, "owner">` keeps this component decoupled from the full enriched user
        // profile — `event.data` satisfies it structurally, and a test fixture needs one field, not
        // eleven.
        owner?: { username?: string | null } | null;
      })
    | null
    | undefined;
  slots: Slots | undefined;
  isPlatform: boolean;
};

/**
 * Web-only orientation block (QW2) shown in the booker's event panel when no slot is available
 * within the acte's window, so the dead end becomes a clear set of next steps:
 * - join the waitlist (when the acte opted in via `waitlistThresholdDays`);
 * - a practitioner-configured fallback message (`noAvailabilityFallbackMessage`), rendered as
 *   escaped plain text — never HTML.
 *
 * Renders nothing unless ALL gates pass: the `waitlist` feature flag is on, we are not on
 * platform/embed, the loaded schedule has no availability within the window, and at least one
 * orientation (waitlist CTA or fallback message) is configured — so the public flow is strictly
 * unchanged for everyone else.
 */
/** Per-browser marker that this visitor already joined the waitlist for a given event type. */
const registeredStorageKey = (eventId: number) => `waitlist-joined:${eventId}`;

export const WaitlistCTA = ({ event, slots, isPlatform }: WaitlistCTAProps) => {
  const { t } = useLocale();
  const flags = useFlags();
  const [open, setOpen] = useState(false);
  const [registered, setRegistered] = useState(false);
  const eventId = event?.id;
  const thresholdDays = event?.waitlistThresholdDays ?? null;
  const fallbackMessage = event?.noAvailabilityFallbackMessage?.trim() || null;
  // Waitlist threshold when the acte opted in, otherwise a default horizon so the fallback message
  // alone can still be offered; null (no orientation configured) keeps the hook returning false.
  const detectionDays = thresholdDays ?? (fallbackMessage ? DEFAULT_FALLBACK_THRESHOLD_DAYS : null);
  const noAvailability = useNoAvailabilityWithinThreshold(slots, detectionDays);
  // W-04 — a cabinet (team) event type has no owner, so the matching job could never build the booking
  // link and nobody would ever be notified. The server refuses those registrations; hiding the button
  // here is what keeps the patient from filling the form and ticking the RGPD consent only to be told
  // "NOT_FOUND". ⚠️ Only the BUTTON is gated: the practitioner's fallback message (QW2) must keep
  // showing for a cabinet acte — it is precisely the orientation left to those patients.
  const offerWaitlist = Boolean(thresholdDays) && Boolean(event?.owner);

  // Rehydrate the "already joined" state client-side (localStorage is unavailable during SSR). Best-effort:
  // it hides the CTA for a returning visitor on THIS browser — the server dedup stays the source of truth.
  useEffect(() => {
    if (!eventId) return;
    try {
      setRegistered(window.localStorage.getItem(registeredStorageKey(eventId)) === "1");
    } catch {
      // localStorage can throw (private mode, disabled) — degrade gracefully to showing the CTA.
    }
  }, [eventId]);

  const onRegistered = () => {
    setRegistered(true);
    if (!eventId) return;
    try {
      window.localStorage.setItem(registeredStorageKey(eventId), "1");
    } catch {
      // Non-fatal: the confirmation still shows for this session even if we can't persist it.
    }
  };

  if (isPlatform || !flags.waitlist || !eventId || !noAvailability) return null;
  if (!offerWaitlist && !fallbackMessage) return null;

  return (
    <div className="border-subtle bg-default mx-5 mt-4 rounded-md border p-4">
      <p className="text-emphasis text-sm font-semibold">{t("waitlist_cta_title")}</p>
      {offerWaitlist &&
        (registered ? (
          <p className="text-subtle mt-1 text-sm">{t("waitlist_you_are_registered")}</p>
        ) : (
          <>
            <p className="text-subtle mt-1 text-sm">{t("waitlist_cta_description")}</p>
            <Button color="secondary" className="mt-3 w-full justify-center" onClick={() => setOpen(true)}>
              {t("waitlist_cta_button")}
            </Button>
            <WaitlistRegistrationDialog
              open={open}
              onOpenChange={setOpen}
              eventTypeId={eventId}
              eventTitle={event.title}
              // Offer the WhatsApp opt-in only when the programme is globally live (US-11); off by default, so
              // patients are never shown a channel that cannot yet send. Same flag map the CTA already reads.
              whatsAppEnabled={!!flags["whatsapp-notifications"]}
              onRegistered={onRegistered}
            />
          </>
        ))}
      {fallbackMessage && (
        <p className="text-subtle mt-3 whitespace-pre-line text-sm">{fallbackMessage}</p>
      )}
    </div>
  );
};

export default WaitlistCTA;
